import * as THREE from 'three';
import { SHADOW_CASTER_LAYER } from '../environment/scenery-shadows.js';

/** Parts smaller than this (bounding radius, metres) cast no shadow: buckles, rivets, teeth and eyes are lost in a 2048 map spanning tens of metres. */
const MINOR_RADIUS = .1;
/** Surfaces that lie on or inside another that already casts the same shadow: lips inside the face, emblems painted on cloth. */
const COVERED = new Set(['lips', 'emblem']);

/** The depth material of every plain skinned surface; three's own is shared with rigid parts and the world. */
let skinnedDepth: THREE.MeshDepthMaterial | undefined;

/** Whether a part is too small, or too covered, to cast a figure's shadow; world matrices must be current. */
export function minorShadow(object: THREE.Mesh) {
  const materials: THREE.Material[] = Array.isArray(object.material) ? object.material : [object.material];
  if (!object.geometry.boundingSphere) object.geometry.computeBoundingSphere();
  const scale = object.getWorldScale(new THREE.Vector3());
  const radius = (object.geometry.boundingSphere?.radius ?? 0) * Math.max(Math.abs(scale.x), Math.abs(scale.y), Math.abs(scale.z));
  return radius < MINOR_RADIUS || materials.every(material => COVERED.has(material.name));
}

/**
 * Trims a finished figure's shadow casting: minor and covered parts stop casting, and every plain skinned surface draws its shadow
 * with a depth material only skinned surfaces use. Three keeps one program per material and rebuilds the program choice whenever
 * consecutive shadow draws switch between skinned and rigid geometry, which cost about a millisecond a frame in a crowded battle.
 */
export function trimFigureShadows(root: THREE.Object3D) {
  root.updateMatrixWorld(true);
  root.traverse(object => {
    if (!(object instanceof THREE.Mesh) || !object.castShadow) return;
    const materials: THREE.Material[] = Array.isArray(object.material) ? object.material : [object.material];
    if (minorShadow(object)) { object.castShadow = false; return; }
    if (!(object instanceof THREE.SkinnedMesh) || object.customDepthMaterial || Object.keys(object.geometry.morphAttributes).length || materials.length !== 1) return;
    // Three copies these from the surface onto any depth material; ones that change its program keep three's own handling.
    const material = materials[0] as THREE.MeshStandardMaterial;
    if (material.alphaTest > 0 || material.alphaToCoverage || material.alphaMap || material.displacementMap || material.clipShadows || material.clippingPlanes?.length) return;
    object.customDepthMaterial = skinnedDepth ??= new THREE.MeshDepthMaterial();
  });
}

/** Whether `material` is the shared skinned depth material, which a corpse baked into rigid geometry must stop using. */
export function isSkinnedShadowDepth(material: THREE.Material | undefined) { return material !== undefined && material === skinnedDepth; }

/** Draws nothing in the view: a head shadow lives on `SHADOW_CASTER_LAYER`, which only the shadow pass sees. */
const unseen = new THREE.MeshBasicMaterial({ colorWrite: false, depthWrite: false }); unseen.dispose = () => {};

/** The head's shadow surface is welded on a grid this fine, in metres: a shadow map texel covers more than that at any zoom. */
const SHADOW_CELL = .03;
/** Merges the vertices in each grid cell into its first one, with that one's skinning, and drops the triangles that collapse. */
function weld(positions: number[], indices: number[], skinIndex: number[], skinWeight: number[]) {
  const cells = new Map<string, number>(), remap: number[] = [], out = { positions: [] as number[], indices: [] as number[], skinIndex: [] as number[], skinWeight: [] as number[] };
  for (let i = 0; i < positions.length / 3; i++) {
    const key = `${Math.round(positions[i * 3] / SHADOW_CELL)},${Math.round(positions[i * 3 + 1] / SHADOW_CELL)},${Math.round(positions[i * 3 + 2] / SHADOW_CELL)}`;
    let kept = cells.get(key);
    if (kept === undefined) {
      cells.set(key, kept = out.positions.length / 3);
      out.positions.push(positions[i * 3], positions[i * 3 + 1], positions[i * 3 + 2]);
      for (let c = 0; c < 4; c++) { out.skinIndex.push(skinIndex[i * 4 + c]); out.skinWeight.push(skinWeight[i * 4 + c]); }
    }
    remap.push(kept);
  }
  for (let i = 0; i < indices.length; i += 3) {
    const a = remap[indices[i]], b = remap[indices[i + 1]], c = remap[indices[i + 2]];
    if (a !== b && b !== c && a !== c) out.indices.push(a, b, c);
  }
  return out;
}

/**
 * The surfaces of a figure's head (face, hair, headwear) cast one shadow between them: their positions and skin weights merged into a
 * depth-only surface on `SHADOW_CASTER_LAYER`, and the surfaces themselves stop casting. A head is drawn into the shadow map once
 * instead of three or four times; in that blur nobody can tell hair from face. It carries the head's part tags, so severing hides it.
 */
export function foldHeadShadows(root: THREE.Object3D) {
  const heads: THREE.SkinnedMesh[] = [];
  root.traverse(object => {
    if (object instanceof THREE.SkinnedMesh && object.isSkinnedMesh && object.castShadow && object.visible && object.userData.nimblezParts && !object.userData.nimblezParts.tattooRegion
      && !Object.keys(object.geometry.morphAttributes).length && !Array.isArray(object.material)) heads.push(object);
  });
  const first = heads[0], members = heads.filter(mesh => mesh.skeleton === first?.skeleton && mesh.bindMatrix.equals(first.bindMatrix) && mesh.bindMode === first.bindMode);
  if (members.length < 2) return;
  const geometry = new THREE.BufferGeometry(), positions: number[] = [], indices: number[] = [], skinIndex: number[] = [], skinWeight: number[] = [];
  for (const mesh of members) {
    const source = mesh.geometry, offset = positions.length / 3, position = source.getAttribute('position'), index = source.getAttribute('skinIndex'), weight = source.getAttribute('skinWeight');
    for (let i = 0; i < position.count; i++) {
      positions.push(position.getX(i), position.getY(i), position.getZ(i));
      for (let c = 0; c < 4; c++) { skinIndex.push(index.getComponent(i, c)); skinWeight.push(weight.getComponent(i, c)); }
    }
    if (source.index) for (let i = 0; i < source.index.count; i++) indices.push(source.index.getX(i) + offset);
    else for (let i = 0; i < position.count; i++) indices.push(i + offset);
    mesh.castShadow = false;
  }
  const welded = weld(positions, indices, skinIndex, skinWeight);
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(welded.positions, 3)); geometry.setIndex(welded.indices);
  geometry.setAttribute('skinIndex', new THREE.Uint16BufferAttribute(welded.skinIndex, 4)); geometry.setAttribute('skinWeight', new THREE.Float32BufferAttribute(welded.skinWeight, 4));
  const shadow = new THREE.SkinnedMesh(geometry, unseen);
  shadow.name = 'head-shadow'; shadow.castShadow = true; shadow.receiveShadow = false; shadow.layers.set(SHADOW_CASTER_LAYER);
  shadow.userData.nimblezParts = { ...first.userData.nimblezParts }; shadow.userData.headShadow = true;
  shadow.customDepthMaterial = skinnedDepth ??= new THREE.MeshDepthMaterial();
  root.add(shadow); shadow.bind(first.skeleton, first.bindMatrix); shadow.matrixAutoUpdate = false;
  shadow.boundingSphere = new THREE.Sphere(new THREE.Vector3(0, 1, 0), 2.4);
}
